import React, { useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import { Link } from "react-router-dom";

const Form = () => {
    const navigate = useNavigate()
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        gender: "",
        email: "",
        phone: "",
        member: "",
        invitedBy: "",
        affliate: "",
        attended: "",
        localChurch: "",
    });

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const toInitialCaps = (value: string) => value
        .trim()
        .toLowerCase()
        .split(/(\s+|[-'])/)
        .map((part) => {
            if (/^\s+$/.test(part) || part === "-" || part === "'") return part;
            return part ? part.charAt(0).toUpperCase() + part.slice(1) : part;
        })
        .join("");

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        // Basic validation
        if (!formData.firstName.trim()) {
            toast.error("First name is required");
            return;
        }
        if (!formData.lastName.trim()) {
            toast.error("Last name is required");
            return;
        }
        if (!formData.gender) {
            toast.error("Please select a gender");
            return;
        }
        if (!formData.email.trim()) {
            toast.error("Email is required");
            return;
        }
        if (!formData.phone.trim()) {
            toast.error("Phone number is required");
            return;
        }
        if (!formData.member) {
            toast.error("Please select if you are a member");
            return;
        }
        if (formData.member === "no" && !formData.invitedBy.trim()) {
            toast.error("Please provide who invited you");
            return;
        }
        if (formData.member === "yes" && !formData.localChurch.trim()) {
            toast.error("Please provide your local church");
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await fetch("https://thenewbrook.com/api/haystack/form-handler.php", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(formData),
            });

            const result = await response.json();

            if (result.success) {
                const registration = {
                    fullName: `${toInitialCaps(formData.firstName)} ${toInitialCaps(formData.lastName)}`,
                    passId: typeof result.passId === "string" ? result.passId : "",
                };

                if (!registration.passId) {
                    toast.error("Your pass ID could not be generated. Please contact support.");
                    setIsSubmitting(false);
                    return;
                }

                sessionStorage.setItem("haystack_registration", JSON.stringify(registration));
                navigate('/success', { state: registration });
            } else {
                toast.error(result.message || result.error || "Something went wrong");
                setIsSubmitting(false);
            }
        } catch (err) {
            toast.error("Server error. Please try again later.");
            setIsSubmitting(false);
        }
    };

    return (
        <div className="flex justify-center items-center flex-col py-8 px-10 w-full ">
            <h1 className="font-clashMedium text-3xl md:text-4xl underline underline-offset-8 text-center">
                Registration form
            </h1>
            <p className="font-clashRegular py-6 text-lg md:text-xl text-center">
                Please enter the following information carefully.
            </p>

            <form
                onSubmit={handleSubmit}
                className="flex flex-col w-full py-4 px-2 md:px-24 gap-y-8"
            >
                {/* First + Last Name */}
                <div className="flex flex-col md:flex-row md:space-x-6 gap-y-6 md:gap-y-0">
                    <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        placeholder="First name"
                        className="flex-1 border-b text-lg md:text-2xl font-clashRegular placeholder:text-blackX/45 py-3 md:py-4 bg-transparent border-black outline-none"
                    />
                    <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        placeholder="Last name"
                        className="flex-1 border-b text-lg md:text-2xl font-clashRegular placeholder:text-blackX/45 py-3 md:py-4 bg-transparent border-black outline-none"
                    />
                </div>

                {/* Gender */}
                <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    className="border-b w-full text-lg md:text-2xl font-clashRegular py-3 md:py-4 bg-transparent border-black outline-none"
                >
                    <option value="" disabled>
                        Gender
                    </option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                </select>

                {/* Email */}
                <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Email Address"
                    className="border-b w-full text-lg md:text-2xl font-clashRegular py-3 placeholder:text-blackX/45 md:py-4 bg-transparent border-black outline-none"
                />

                {/* Phone */}
                <input
                    type="number"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="Phone No."
                    className="border-b w-full text-lg md:text-2xl font-clashRegular placeholder:text-blackX/45 py-3 md:py-4 bg-transparent border-black outline-none"
                />

                <div className="flex flex-col gap-y-4 mt-4">
                    <p className="font-clashRegular text-lg md:text-2xl">
                        Did you attend the last edition of Haystack ?
                    </p>
                    <div className="flex flex-col sm:flex-row sm:space-x-6 gap-y-3 sm:gap-y-0">
                        <label className="flex items-center space-x-2">
                            <input
                                type="radio"
                                name="attended"
                                value="yes"
                                checked={formData.attended === "yes"}
                                onChange={handleChange}
                                className="form-radio text-blue-600"
                            />
                            <span className="font-clashRegular text-base md:text-xl">Yes</span>
                        </label>

                        <label className="flex items-center space-x-2">
                            <input
                                type="radio"
                                name="attended"
                                value="no"
                                checked={formData.attended === "no"}
                                onChange={handleChange}
                                className="form-radio text-red-600"
                            />
                            <span className="font-clashRegular text-base md:text-xl">No</span>
                        </label>
                    </div>
                </div>

                {/* Member radio */}
                <div className="flex flex-col gap-y-4 mt-4">
                    <p className="font-clashRegular text-lg md:text-2xl">
                        Are you a member of The NewBrook Church?
                    </p>
                    <div className="flex flex-col sm:flex-row sm:space-x-6 gap-y-3 sm:gap-y-0">
                        <label className="flex items-center space-x-2">
                            <input
                                type="radio"
                                name="member"
                                value="yes"
                                checked={formData.member === "yes"}
                                onChange={handleChange}
                                className="form-radio text-blue-600"
                            />
                            <span className="font-clashRegular text-base md:text-xl">Yes</span>
                        </label>

                        <label className="flex items-center space-x-2">
                            <input
                                type="radio"
                                name="member"
                                value="no"
                                checked={formData.member === "no"}
                                onChange={handleChange}
                                className="form-radio text-red-600"
                            />
                            <span className="font-clashRegular text-base md:text-xl">No</span>
                        </label>
                    </div>
                </div>

                

                {/* Invited by */}
                {formData.member === "no" && (
                    <div className="flex flex-col gap-5 mt-4">

                        <div className="flex flex-col">
                            <p className="font-clashRegular text-lg md:text-2xl">
                                How did you hear about the meeting?
                            </p>
                            <input
                                type="text"
                                name="invitedBy"
                                value={formData.invitedBy}
                                onChange={handleChange}
                                className="border-b w-full text-lg placeholder:text-blackX/45 md:text-2xl font-clashRegular py-3 bg-transparent border-black outline-none"
                            />

                        </div>

                        <div className="flex flex-col mt-4">
                            <p className="font-clashRegular text-lg md:text-2xl">
                                Which church, ministry or religious organisation are you affliated with?
                            </p>
                            <input
                                type="text"
                                name="affliate"
                                value={formData.affliate}
                                onChange={handleChange}
                                className="border-b w-full text-lg placeholder:text-blackX/45 md:text-2xl font-clashRegular py-3 bg-transparent border-black outline-none"
                            />

                        </div>


                    </div>
                )}

                {formData.member === "yes" && (
                    <div className="flex flex-col gap-5 mt-4">
                        <input
                            type="text"
                            name="localChurch"
                            value={formData.localChurch}
                            onChange={handleChange}
                            placeholder="State your local church"
                            className="border-b w-full text-lg placeholder:text-blackX/45 md:text-2xl font-clashRegular py-3 md:py-4 bg-transparent border-black outline-none"
                        />
                    </div>
                )}

                {/* Submit */}
                <div className="flex flex-col gap-y-12 justify-center items-center mt-10">
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="sm:w-auto px-10 sm:px-20 py-3 md:py-4 bg-greenX font-clashRegular text-white text-lg md:text-2xl shadow-y rounded-xl w-min disabled:cursor-wait disabled:opacity-60"
                    >
                        {isSubmitting ? (
                            <span className="inline-flex items-center gap-3">
                                <span className="h-5 w-5 animate-spin rounded-full border-2 border-white/35 border-t-white" aria-hidden="true" />
                                Submitting…
                            </span>
                        ) : "Register"}
                    </button>

                    <Link to='/' className="underline text-lg text-blue mt-4">
                        ← Go to Homepage
                    </Link>
                </div>
            </form>

            {/* Toasts */}
            <ToastContainer position="top-right" autoClose={3000} />
        </div>
    );
};

export default Form;
